<script setup lang="ts">
import {
  SigningFileType,
  SigningRequestSigneeRole,
  SigningRequestStatus,
  WebsocketSubscriptionTarget,
  type FilterableSearchQuery,
  type ParticipantInDb,
  type SigningRequestWithRelationsInDb,
} from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaAvatarStack, {
  type GaiaAvatarStackItem,
} from '@/components/design/composite/GaiaAvatarStack.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaCheckChip from '@/components/design/base/GaiaCheckChip.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import {
  participantStatusMap,
  signedParticipantStates,
  signingRequestStatusMap,
} from '@/components/design/statusMaps';
import { SignatureRequestService } from '@/services/contractManagement/signatureRequestService';
import { useSigningProviderOptions } from '@/composables/useSigningProviderOptions';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useSigningRequestWebSocket } from '@/composables/useSigningRequestWebSocket';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { isEnumValue } from '@/utils/enumUtils';
import { initialsOf } from '@/utils/initials';
import { participantName, participantsInRole } from '@/utils/signingParticipant';
import { logger } from '@/utils/logger';
import { IconDocument, IconMore } from '@/components/design/icons';
import { NFlex, NIcon, NTooltip, type DataTableColumns, type DropdownOption } from 'naive-ui';
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { signingRequestEditRoute, signingRequestRoute } from '@/router/links';

/**
 * The rebuilt signature-requests list (Document Signing mock): the status
 * filter is the tab row rather than a filter bar, and every row carries its
 * signers as faces plus a ⋯ menu. Search, paging, sort and the archived
 * toggle stay in the URL through `useListQuerySync`, so list → detail → back
 * returns to the same view.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const organizationStore = useOrganizationStore();

const requests = ref<SigningRequestWithRelationsInDb[]>([]);
const totalCount = ref(0);
const loading = ref(false);
const busyUid = ref<string | null>(null);
/** Statuses the backend reports for this organisation, used to build the tabs. */
const availableStatuses = ref<SigningRequestStatus[]>([]);

const {
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  includeArchived,
  columnFilters,
} = useListQuerySync({ includeArchived: false });

const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount);

// ── Signing availability ────────────────────────────────────────────────────

const { providerOptions } = useSigningProviderOptions();
const hasSigningProvider = computed(() => providerOptions.value.length > 0);

// ── Status tabs ─────────────────────────────────────────────────────────────
//
// The mock draws five fixed tabs, but the backend has eight statuses and the
// old page let the filter bar reach every one of them. So the row is the fixed
// order below, filtered to what this organisation actually has — the tabs are
// the status filter, with nothing dropped.
const STATUS_ORDER: SigningRequestStatus[] = [
  SigningRequestStatus.DRAFT,
  SigningRequestStatus.PENDING,
  SigningRequestStatus.WAITING_FOR_SIGNATURES,
  SigningRequestStatus.SIGNED,
  SigningRequestStatus.REJECTED,
  SigningRequestStatus.CANCELLED,
  SigningRequestStatus.EXPIRED,
  SigningRequestStatus.ERROR,
];

/**
 * The status the tab row sits on. `columnFilters` is typed as a total record,
 * so a key that may be absent is read through `in` rather than `?.`.
 */
const statusFilter = computed<SigningRequestStatus[]>(() =>
  'status' in columnFilters.value
    ? columnFilters.value.status.filter(
        (value): value is SigningRequestStatus =>
          isEnumValue(SigningRequestStatus, value) && STATUS_ORDER.includes(value)
      )
    : []
);

const activeStatus = computed<string>(() =>
  statusFilter.value.length > 0 ? statusFilter.value[0] : 'all'
);

const statusTabs = computed<GaiaSegmentedOption[]>(() => [
  { label: 'All', value: 'all' },
  ...STATUS_ORDER.filter(status => availableStatuses.value.includes(status)).map(status => ({
    label: signingRequestStatusMap[status].label,
    value: status,
    tone: signingRequestStatusMap[status].tone,
  })),
]);

function selectStatus(value: string): void {
  columnFilters.value = value === 'all' ? {} : { status: [value] };
}

// ── Context: a request started from a contract keeps that context ──────────

const contractUidContext = computed(() =>
  typeof route.query.contractUid === 'string' ? route.query.contractUid : null
);

// ── Fetching ────────────────────────────────────────────────────────────────

/** `quiet` refetches in place (a live update) instead of blanking the table to a skeleton. */
async function fetchRequests({ quiet = false }: { quiet?: boolean } = {}): Promise<void> {
  if (!quiet) loading.value = true;

  try {
    const query: FilterableSearchQuery = {
      text: searchQuery.value.trim(),
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value,
      similarity_threshold: 0.5,
      column_filters: statusFilter.value.length > 0 ? { status: [...statusFilter.value] } : {},
    };

    const response = await SignatureRequestService.search(query, includeArchived.value);
    requests.value = response.results;
    totalCount.value = response.result_count;

    // Only widen the tab row: filtering by one status narrows `filter_options`
    // to that status, which would otherwise collapse the row to a single tab.
    const filterOptions = response.filter_options as Record<string, (string | null)[]>;
    const options = 'status' in filterOptions ? filterOptions.status : [];
    const reported = options.filter(
      (value): value is SigningRequestStatus =>
        isEnumValue(SigningRequestStatus, value) && STATUS_ORDER.includes(value)
    );
    availableStatuses.value = [...new Set([...availableStatuses.value, ...reported])];
  } catch (error) {
    logger.error('Error fetching signature requests:', error);
    message.error(error instanceof Error ? error.message : 'Failed to load signature requests.');
  } finally {
    loading.value = false;
  }
}

/** The org's available providers come with it — re-read in case an integration changed. */
async function refreshSigningAvailability(): Promise<void> {
  await organizationStore.fetchActiveOrganization();
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchRequests();
}

watch([currentPage, pageSize], () => {
  if (currentPage.value > 0 && pageSize.value > 0) {
    void fetchRequests();
  }
});

watch(columnFilters, () => {
  currentPage.value = 1;
  void fetchRequests();
});

function toggleIncludeArchived(value: boolean): void {
  includeArchived.value = value;
  currentPage.value = 1;
  void fetchRequests();
}

function handleSortChange(
  sorter: { order: false | 'ascend' | 'descend'; columnKey: string } | null
): void {
  if (!sorter) return;

  if (sorter.order === false) {
    sortKey.value = 'created_at';
    sortDirection.value = 'desc';
    sortActivated.value = false;
  } else {
    sortActivated.value = true;
    sortKey.value = sorter.columnKey;
    sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  }

  currentPage.value = 1;
  void fetchRequests();
}

// ── Row helpers ─────────────────────────────────────────────────────────────

function requestName(row: SigningRequestWithRelationsInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return row.name?.trim() || 'Unnamed request';
}

/** Second line under the name: the signable documents, else the contract. */
function requestSubtitle(row: SigningRequestWithRelationsInDb): string {
  const documents = (row.vault_file_revisions ?? [])
    .filter(entry => entry.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT)
    .map(entry => entry.vault_file_revision.file_name.trim())
    .filter(Boolean);

  return [documents.join(', '), row.contract?.name.trim()].filter(Boolean).join(' · ') || '—';
}

function documentCount(row: SigningRequestWithRelationsInDb): number {
  return (row.vault_file_revisions ?? []).length;
}

function remindableParticipants(row: SigningRequestWithRelationsInDb): ParticipantInDb[] {
  return (row.participants ?? []).filter(
    participant => participant.status && participantStatusMap[participant.status].canRemind
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';

  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** A count cell with the names behind it on hover — the mock's Approvers column. */
function renderRoleCount(
  row: SigningRequestWithRelationsInDb,
  role: SigningRequestSigneeRole
): VNode {
  const people = participantsInRole(row.participants, role);

  if (people.length === 0) {
    return h(GaiaText, { size: 'control', tone: 'muted' }, () => '—');
  }

  const label = h(
    GaiaText,
    { size: 'control', tone: 'tertiary', style: { fontVariantNumeric: 'tabular-nums' } },
    () => String(people.length)
  );

  return h(
    NTooltip,
    { trigger: 'hover' },
    { trigger: () => label, default: () => people.map(participantName).join(', ') }
  );
}

// ── Row actions ─────────────────────────────────────────────────────────────

type PendingAction = { kind: 'cancel' | 'delete'; row: SigningRequestWithRelationsInDb };

const pendingAction = ref<PendingAction | null>(null);

const confirmCopy = computed(() => {
  if (!pendingAction.value) return { title: '', body: '', confirm: '' };

  return pendingAction.value.kind === 'cancel'
    ? {
        title: 'Cancel this signature request?',
        body: 'Signers lose access to their signing links immediately, and already-collected signatures are void.',
        confirm: 'Cancel request',
      }
    : {
        title: 'Delete this draft?',
        body: 'The draft and everything prepared in it are removed. Nothing was sent, so no signer is affected.',
        confirm: 'Delete draft',
      };
});

function rowMenuOptions(row: SigningRequestWithRelationsInDb): DropdownOption[] {
  const isDraft = row.status === SigningRequestStatus.DRAFT;
  const canCancel =
    row.status === SigningRequestStatus.PENDING ||
    row.status === SigningRequestStatus.WAITING_FOR_SIGNATURES;

  return [
    // A draft opens in the wizard (see `requestRoute`).
    { label: isDraft ? 'Continue editing' : 'Open request', key: 'open' },
    ...(remindableParticipants(row).length > 0 ? [{ label: 'Send reminders', key: 'remind' }] : []),
    { label: row.archived ? 'Unarchive' : 'Archive', key: 'archive' },
    ...(canCancel || isDraft ? [{ type: 'divider', key: 'sep' }] : []),
    ...(canCancel ? [{ label: 'Cancel request', key: 'cancel' }] : []),
    ...(isDraft ? [{ label: 'Delete draft', key: 'delete', props: gaiaDangerOption }] : []),
  ];
}

async function handleMenuSelect(key: string, row: SigningRequestWithRelationsInDb): Promise<void> {
  switch (key) {
    case 'open':
      openRequest(row);
      return;
    case 'remind':
      await sendReminders(row);
      return;
    case 'archive':
      await toggleArchive(row);
      return;
    case 'cancel':
      pendingAction.value = { kind: 'cancel', row };
      return;
    case 'delete':
      pendingAction.value = { kind: 'delete', row };
      return;
  }
}

async function sendReminders(row: SigningRequestWithRelationsInDb): Promise<void> {
  const open = remindableParticipants(row);
  busyUid.value = row.uid;

  try {
    for (const participant of open) {
      await SignatureRequestService.sendParticipantReminder(row.uid, participant.uid);
    }
    message.success(
      open.length === 1 ? 'Reminder sent.' : `Reminders sent to ${open.length} participants.`
    );
  } catch (error) {
    logger.error('Error sending reminders:', error);
    message.error(error instanceof Error ? error.message : 'Failed to send reminders.');
  } finally {
    busyUid.value = null;
  }
}

async function toggleArchive(row: SigningRequestWithRelationsInDb): Promise<void> {
  busyUid.value = row.uid;

  try {
    await SignatureRequestService.setArchived(row.uid, !row.archived);
    message.success(row.archived ? 'Signature request unarchived.' : 'Signature request archived.');
    await fetchRequests();
  } catch (error) {
    logger.error('Error archiving signature request:', error);
    message.error(error instanceof Error ? error.message : 'Failed to update signature request.');
  } finally {
    busyUid.value = null;
  }
}

async function confirmPendingAction(): Promise<void> {
  const pending = pendingAction.value;
  if (!pending) return;

  busyUid.value = pending.row.uid;

  try {
    if (pending.kind === 'cancel') {
      await SignatureRequestService.cancel(pending.row.uid, { reason: 'other' });
      message.success('Signature request cancelled.');
    } else {
      await SignatureRequestService.remove(pending.row.uid);
      message.success('Draft deleted.');
    }
    pendingAction.value = null;
    await fetchRequests();
  } catch (error) {
    logger.error('Error updating signature request:', error);
    message.error(error instanceof Error ? error.message : 'Failed to update signature request.');
  } finally {
    busyUid.value = null;
  }
}

// ── Navigation ──────────────────────────────────────────────────────────────

/** A draft opens where it is worked on — the wizard — like a contract-generation draft. */
function requestRoute(row: SigningRequestWithRelationsInDb): RouteLocationRaw {
  if (row.status === SigningRequestStatus.DRAFT) return signingRequestEditRoute(row.uid);
  return { ...signingRequestRoute(row.uid), query: route.query };
}

function openRequest(row: SigningRequestWithRelationsInDb): void {
  void router.push(requestRoute(row));
}

function startRequest(): void {
  if (contractUidContext.value) {
    void router.push({
      name: 'signing-contract-request-new',
      params: { uid: contractUidContext.value },
      query: { from: 'overview' },
    });
    return;
  }

  void router.push({ name: 'signing-request-new', query: { from: 'overview' } });
}

// ── Table ───────────────────────────────────────────────────────────────────

function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (!sortActivated.value || sortKey.value !== key) return false;
  return sortDirection.value === 'asc' ? 'ascend' : 'descend';
}

const columns = computed((): DataTableColumns<SigningRequestWithRelationsInDb> => [
  {
    title: 'Request',
    key: 'name',
    minWidth: 240,
    render: row =>
      h(NFlex, { vertical: true, size: 3, wrap: false, style: { minWidth: 0 } }, () => [
        h(NFlex, { align: 'center', size: 8, wrap: false, style: { minWidth: 0 } }, () => [
          // A real link so cmd- and middle-click open the request in a new tab.
          // It is the flex item next to the tag now, so it carries the min-width
          // half of the truncate recipe.
          h(
            RouterLink,
            {
              to: requestRoute(row),
              style: 'color: inherit; text-decoration: none; display: block; min-width: 0',
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
              },
            },
            {
              default: () =>
                h(
                  GaiaText,
                  {
                    size: 'item',
                    weight: 'semibold',
                    truncate: true,
                    title: requestName(row),
                    style: { minWidth: 0 },
                  },
                  () => requestName(row)
                ),
            }
          ),
          row.archived ? h(GaiaTag, { variant: 'muted', size: 'small' }, () => 'Archived') : null,
        ]),
        h(
          GaiaText,
          { size: 'micro', tone: 'faint', truncate: true, title: requestSubtitle(row) },
          () => requestSubtitle(row)
        ),
      ]),
  },
  {
    title: 'Created',
    key: 'created_at',
    width: 150,
    sorter: true,
    sortOrder: sortOrderFor('created_at'),
    render: row =>
      h(
        GaiaText,
        { size: 'control', tone: 'tertiary', style: { fontVariantNumeric: 'tabular-nums' } },
        () => formatDate(row.created_at)
      ),
  },
  {
    title: 'Docs',
    key: 'documents',
    width: 92,
    render: row =>
      h(NFlex, { align: 'center', size: 6, wrap: false }, () => [
        h(NIcon, { component: IconDocument, size: 14, depth: 4 }),
        h(GaiaText, { size: 'control', tone: 'tertiary' }, () => String(documentCount(row))),
      ]),
  },
  {
    title: 'Status',
    key: 'status',
    width: 168,
    sorter: true,
    sortOrder: sortOrderFor('status'),
    render: row =>
      row.status
        ? h(
            GaiaStatusChip,
            { tone: signingRequestStatusMap[row.status].tone },
            () => signingRequestStatusMap[row.status as SigningRequestStatus].label
          )
        : h(GaiaText, { size: 'control', tone: 'muted' }, () => '—'),
  },
  {
    title: 'Signers',
    key: 'signers',
    minWidth: 200,
    render: (row): VNode => {
      const signers = participantsInRole(row.participants, SigningRequestSigneeRole.SIGNER);

      if (signers.length === 0) {
        return h(GaiaText, { size: 'control', tone: 'muted' }, () => 'No signers yet');
      }

      const signed = signers.filter(participant =>
        Boolean(participant.status && signedParticipantStates.has(participant.status))
      ).length;

      const items: GaiaAvatarStackItem[] = signers.map(participant => ({
        key: participant.uid,
        initials: initialsOf(participantName(participant)),
        name: participantName(participant),
      }));

      return h(GaiaAvatarStack, { items, label: `${signed}/${signers.length} signed` });
    },
  },
  // The cell is a single count, but the uppercase tracked header sets the
  // floor: "APPROVERS" measures ~85px at micro/700 with 0.09em tracking, and
  // the th's 18px side padding takes 36 more — under ~120 it wraps mid-word.
  {
    title: 'Approvers',
    key: 'approvers',
    width: 140,
    render: row => renderRoleCount(row, SigningRequestSigneeRole.APPROVER),
  },
  {
    title: 'Followers',
    key: 'followers',
    width: 140,
    render: row => renderRoleCount(row, SigningRequestSigneeRole.FOLLOWER),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      h(
        GaiaDropdown,
        {
          options: rowMenuOptions(row),
          onSelect: (key: string) => void handleMenuSelect(key, row),
        },
        () =>
          h(
            GaiaIconButton,
            {
              quaternary: true,
              dimension: 28,
              loading: busyUid.value === row.uid,
              'aria-label': `Actions for ${requestName(row)}`,
              onClick: (event: MouseEvent) => {
                event.stopPropagation();
              },
            },
            () => h(NIcon, { component: IconMore })
          )
      ),
  },
]);

/** Clicks on a control inside the row are the control's, not the row's. */
function handleRowClick(row: SigningRequestWithRelationsInDb, event: MouseEvent): void {
  const target = event.target as HTMLElement;
  if (target.closest('button') || target.closest('a')) return;

  openRequest(row);
}

const hasFilters = computed(
  () => searchQuery.value.trim().length > 0 || activeStatus.value !== 'all'
);

function clearFilters(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  columnFilters.value = {};
}

function handleConfirmVisibility(visible: boolean): void {
  if (!visible) {
    pendingAction.value = null;
  }
}

onMounted(() => {
  void refreshSigningAvailability();
  void fetchRequests();
});

// A request created, sent, signed or deleted anywhere in the organisation:
// refetch the page in place, without blanking the table.
useSigningRequestWebSocket({
  target: WebsocketSubscriptionTarget.SIGNING_REQUESTS,
  uid: () => organizationStore.activeOrganization?.uid ?? null,
  onUpdated: () => void fetchRequests({ quiet: true }),
});
</script>

<template>
  <layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.sign }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.signatureRequests }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="20" :wrap="false">
      <n-flex align="flex-start" :size="16">
        <n-flex
          align="baseline"
          :size="10"
          :wrap="false"
          :style="{ flex: '1 1 240px', minWidth: 0 }"
        >
          <gaia-header :level="1">Signature requests</gaia-header>
          <gaia-count-label>
            {{ totalCount === 1 ? '1 request' : `${totalCount} requests` }}
          </gaia-count-label>
        </n-flex>

        <n-flex
          align="center"
          :size="10"
          justify="end"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-search-input
            v-model:value="searchQuery"
            placeholder="Search requests, signers…"
            :debounce="500"
            @search="applySearch"
          />
          <gaia-check-chip :checked="includeArchived" @update:checked="toggleIncludeArchived">
            Include archived
          </gaia-check-chip>
          <!-- Slotted label: "signature request" is the record, but "New
               signature request" is what the signing surface calls the act. -->
          <gaia-create-button
            data-tour="signing.create-request"
            :disabled="!hasSigningProvider"
            @click="startRequest"
          >
            New signature request
          </gaia-create-button>
        </n-flex>
      </n-flex>

      <gaia-banner v-if="!hasSigningProvider" type="warning">
        GAIA Signing is not enabled for this organisation, and no Youtrust integration is connected.
        Contact sales to enable GAIA Signing, or add your own Youtrust integration in
        <gaia-link :to="{ name: 'service-account-token' }">Integrations</gaia-link>.
      </gaia-banner>

      <gaia-banner v-if="contractUidContext" type="info">
        New requests started from here carry the current contract context.
      </gaia-banner>

      <!-- The status filter: the tab row *is* the filter, so there is no
           second filter bar above the table. -->
      <gaia-segmented
        :value="activeStatus"
        :options="statusTabs"
        size="medium"
        @update:value="selectStatus"
      />

      <!-- `table-layout` fixed, so the column widths below are authoritative and
           the cells' `truncate` has a bound to clip against. Left on naive's
           `auto` default, the Request column would hug a long document name and
           grow instead of ellipsing. -->
      <gaia-table
        data-tour="signing.requests-table"
        table-layout="fixed"
        :columns="columns"
        :data="requests"
        :loading="loading"
        :remote="true"
        :pagination="paginationConfig"
        :row-key="(row: SigningRequestWithRelationsInDb) => row.uid"
        :row-props="
          (row: SigningRequestWithRelationsInDb) => ({
            onClick: (event: MouseEvent) => handleRowClick(row, event),
            style: { cursor: 'pointer' },
          })
        "
        @update:sorter="handleSortChange"
      >
        <template #empty>
          <gaia-empty-state v-if="hasFilters" title="No requests match those filters">
            Search covers request names, documents and the people involved.
            <template #actions>
              <gaia-button @click="clearFilters">Clear filters</gaia-button>
            </template>
          </gaia-empty-state>
          <gaia-empty-state v-else title="No signature requests yet">
            Send a contract out for signing and it appears here with its signers, approvers and live
            status. Files come from the repository or are uploaded directly.
            <template #icon><n-icon :component="IconDocument" /></template>
            <template #actions>
              <gaia-create-button
                :icon="false"
                :disabled="!hasSigningProvider"
                @click="startRequest"
              >
                New signature request
              </gaia-create-button>
            </template>
          </gaia-empty-state>
        </template>
      </gaia-table>
    </n-flex>

    <gaia-modal
      :show="pendingAction !== null"
      :title="confirmCopy.title"
      @update:show="handleConfirmVisibility"
    >
      <gaia-text size="body" tone="text">{{ confirmCopy.body }}</gaia-text>
      <template #action>
        <n-flex :size="8" :wrap="false" justify="end">
          <gaia-button @click="pendingAction = null">Keep it</gaia-button>
          <gaia-button type="error" :loading="busyUid !== null" @click="confirmPendingAction">
            {{ confirmCopy.confirm }}
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </layout>
</template>
